// navigation/use-entitlements.ts
// Reads the current user's effective per-feature states from /auth/me. This is
// the single seam between the nav and the entitlement system: if states later
// move to a dedicated flag service, only this hook changes.
import { useEffect, useState } from "react";
import { authApi, type UserMe, type FeatureState } from "@/api/auth";
import type { FeatureId } from "./nav-config";

export type { FeatureState };
export type FeatureStates = Partial<Record<FeatureId, FeatureState>>;

/** State for a feature id, defaulting to "live" when unknown (fail-open). */
export function stateOf(states: FeatureStates, id: FeatureId): FeatureState {
  return states[id] ?? "live";
}

// Last-known effective states, cached so a refresh paints the correct lock
// icons on the first frame instead of flashing fail-open ("live", no locks)
// until /auth/me resolves. Cleared on logout (see auth-service clearStoredFields)
// so a different user never inherits the previous user's locks.
const CACHE_KEY = "nav_feature_locks";

function readCachedStates(): FeatureStates {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as FeatureStates) : {};
  } catch {
    return {};
  }
}

function writeCachedStates(states: FeatureStates): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(states));
  } catch {
    /* ignore */
  }
}

/**
 * Single seam between the nav and the entitlement system. Reads /auth/me once
 * and exposes both the effective per-feature states and the current user (for
 * the workspace menu + admin-only footer gating). If states later move to a
 * dedicated flag service, only this hook changes.
 */
export function useEntitlements(): { states: FeatureStates; user: UserMe | null; loading: boolean } {
  const [states, setStates] = useState<FeatureStates>(readCachedStates);
  const [user, setUser] = useState<UserMe | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    authApi
      .getMe()
      .then((me) => {
        if (!alive) return;
        setUser(me);
        const next = (me.feature_locks as FeatureStates) || {};
        setStates(next);
        writeCachedStates(next);
      })
      .catch(() => {
        // On failure, fail open (everything live) so the nav never traps a user.
        if (alive) setStates({});
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  return { states, user, loading };
}
